import {Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {MessageTranslateService, SiteService} from '../../services';
import {SiteInfo} from '../../services/api/access-sites.interface';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {AceSelectComponent, AceSelectOption} from '../ace-select/ace-select.component';
import {ClientService} from '../../services/api/client.service';
import {Client} from '@app/layout/admin/services/api/client-interfaces';
import {forkJoin, of} from 'rxjs';
import {I18nMessage} from "@shared/api/model/i18n";
import {ObjectUtils} from '@app/shared/util/general/object-utils';
import {CollectionUtils} from '@shared/util/collections/collection-utils';


@Component({
  selector: 'app-site-select',
  templateUrl: './site-select.component.html',
  styleUrls: ['./site-select.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SiteSelectComponent), multi: true}
  ],
  standalone: false
})
export class SiteSelectComponent implements OnInit, OnChanges, ControlValueAccessor {
  private clients: Client[];
  readonly NG_SELECT_SITE_GROUP_BY = 'client_name';

  @Input() sites: SiteInfo[];

  @Input() setDisabled = false;
  @Input() placeholder: string;
  @Input() bindLabel = 'site_name';
  @Input() bindValue: string;
  @Input() appendTo: string;
  @Input() multiSelect = false;
  @Input() maxSelectedLabels: number;
  @Input() maxSelectedShowItemName = true;
  @Input() maxSelectedItemName: I18nMessage;
  @Input() showClearItem = true;
  @Input() truncateLabels = false;
  @Input() closeOnSelect = true;
  @Input() clearable = true;
  @Input() disabledSiteIds: number[];
  @Input() readOnly = false;
  @Input() forceShowGroup = false;
  @Input() compareWith: AceSelectComponent['compareWith'];
  @Input() groupValue: AceSelectComponent['groupValue'];
  @Output() clientsLoaded: EventEmitter<Client[]> = new EventEmitter<Client[]>();
  @Output() sitesLoaded: EventEmitter<SiteInfo[]> = new EventEmitter<SiteInfo[]>();
  @Output() addEvent: EventEmitter<SiteInfo> = new EventEmitter<SiteInfo>();
  @Output() removeEvent: EventEmitter<AceSelectOption<SiteInfo>> = new EventEmitter<AceSelectOption<SiteInfo>>();
  @Output() openEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() closeEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() clearEvent: EventEmitter<any> = new EventEmitter();
  @ViewChild('aceselect', { static: true }) aceselect: AceSelectComponent;

  groupBy: string;
  loading = false;
  processedSites: SiteInfo[];

  onChange: (_) => void = (__) => {};
  onTouched: () => void = () => {};

  constructor(private siteService: SiteService, private clientService: ClientService, private translateService: MessageTranslateService) {
  }

  ngOnInit() {
    this.aceselect.setDisabledState(this.setDisabled);
    this.loadSitesAndClients();
    this.aceselect.registerOnChange(this.aceSelectOnChange);
    this.aceselect.registerOnTouched(this.aceSelectOnTouched);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.setDisabled) {
      this.aceselect.setDisabledState(this.setDisabled);
    }
    if (changes.sites || changes.disabledSiteIds) {
      if (this.sites) {
        this.processSitesAndClients(this.sites, this.clients);
      } else {
        this.processedSites = [];
      }
    }
  }

  aceSelectOnChange = (selected: any) => {
    this.onChange(selected);
  };

  aceSelectOnTouched = () => {
    this.onTouched();
  };

  writeValue(selected: any): void {
    this.aceselect.writeValue(selected);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.aceselect.setDisabledState(isDisabled);
  }

  searchFn(term: string, item: SiteInfo) {
    term = term.toLocaleLowerCase();
    return (item['client_name'] && item['client_name'].toLocaleLowerCase().indexOf(term) > -1) || (item.site_name.toLocaleLowerCase().indexOf(term) > - 1);
  }

  onOpen() {
    this.openEvent.emit();
  }

  onClose() {
    this.closeEvent.emit();
  }

  focus(): void {
    this.aceselect.focus();
  }

  open(): void {
    this.aceselect.open();
  }

  /** throws out group value when group is selected */
  onAdd(item: SiteInfo | any): void {
    this.addEvent.emit(item);
  }

  onRemove(item: AceSelectOption<SiteInfo>): void {
    this.removeEvent.emit(item);
  }

  onClear(): void {
    this.clearEvent.emit();
  }

  private processSitesAndClients(sites: SiteInfo[], clients: Client[]) {
    const sortCriteria = ['site_name'];
    let sitesWithClient: SiteInfo[];
    let sitesWithoutClient: SiteInfo[] = [];
    // Contractor users do not have permissions to see any Clients, so need to check if any clients have been retrieved
    if (clients?.length) {
      const result = this.flattenSitesByClient(sites, clients);
      sitesWithClient = result.sitesWithClient;
      sitesWithoutClient = result.sitesWithoutClient;
      if (result.multipleClients) {
        sortCriteria.unshift('client_name');
        this.groupBy = this.NG_SELECT_SITE_GROUP_BY;
      } else {
        this.groupBy = null;
      }
    } else {
      this.groupBy = null;
      sitesWithClient = sites;
    }
    // client-less sites go after every client group, whatever the label sorts to alphabetically
    const sortedSites = [
      ...ObjectUtils.sortBy<SiteInfo>(sitesWithClient, sortCriteria),
      ...ObjectUtils.sortBy<SiteInfo>(sitesWithoutClient, sortCriteria)
    ];
    this.processedSites = this.applyDisabledSites(sortedSites, this.disabledSiteIds);
  }

  private loadSitesAndClients() {
    this.loading = true;
    forkJoin([
      this.sites == null ? this.siteService.getCachedSiteInfos() : of(this.sites),
      this.clientService.getAllClients()
    ]).subscribe(([sites, clients]: [SiteInfo[], Client[]]) => {
      this.sites = sites;
      this.clients = clients;
      this.processSitesAndClients(sites, clients);
      this.sitesLoaded.next(sites);
      this.clientsLoaded.next(clients);
    }).add(() => this.loading = false);
  }

  private flattenSitesByClient(sites: SiteInfo[], clients: Client[]): {sitesWithClient: SiteInfo[], sitesWithoutClient: SiteInfo[], multipleClients: boolean} {
    const NON_EXISTENT_CLIENT_ID = -1;
    const sitesWithClient: SiteInfo[] = [];
    const sitesWithoutClient: SiteInfo[] = [];
    const uniqueClientIds: Set<number> = new Set();
    const clientsById = CollectionUtils.indexBy(clients, 'id');
    sites.forEach( (currentSite: SiteInfo) => {
      let added = false;
      (currentSite.clients || []).forEach((clientId: number) => {
        const currentClient: Client = clientsById.get(clientId);
        if (currentClient) {
          uniqueClientIds.add(clientId);
          const newSite: SiteInfo = ObjectUtils.cloneDeep(currentSite);
          newSite.clients = [clientId];
          newSite['client_name'] = currentClient.name;
          sitesWithClient.push(newSite);
          added = true;
        }
      });
      if (!added) {
        uniqueClientIds.add(NON_EXISTENT_CLIENT_ID);
        const newSite: SiteInfo = ObjectUtils.cloneDeep(currentSite);
        newSite.clients = [];
        newSite['client_name'] = this.translateService.instant('No Client');
        sitesWithoutClient.push(newSite);
      }
    });

    return {sitesWithClient, sitesWithoutClient, multipleClients: uniqueClientIds.size > 1};
  }

  private applyDisabledSites(sites: SiteInfo[], disabledSiteIds: number[]): SiteInfo[] {
    const disabledSiteIdSet = new Set(disabledSiteIds);
    return disabledSiteIds?.length
      ? sites.map(site => disabledSiteIdSet.has(site.site_id)
        ? ObjectUtils.cloneClassInstance(site, {disabled: true})
        : site)
      : sites;
  }
}
